import { formatAmount as formatDecimalAmount } from '../../utils/orderDisplay'

import { Card } from '@/components/ui/card'

import { SelectField } from '@/components/ui/controls'
import { DataTable } from '@/components/ui/data-table'

import { StatusBadge } from '../products/PageHelpers'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { getTransactions } from '../../services/balance'
import { formatTimestamp } from '../../utils/format'
import { billingReasonText } from '../subscriptions/subscriptionLabels'
import type { BalanceTransaction, Pagination } from '../../types'

const typeLabels: Record<string, string> = {
  PAYMENT: '收款入賬',
  SETTLEMENT: '結算',
  PAYOUT: '結款',
  FREEZE: '凍結',
  UNFREEZE: '解凍',
  CHARGEBACK: '拒付',
  CHARGEBACK_FEE: '拒付手續費',
  CHARGEBACK_RECOVERY: '拒付回款',
  ROLLING_RESERVE_RECOVERY: '循環保證金恢復',
  REFUND: '退款',
  FEE: '費用',
  ROLLING_RESERVE: '循環保證金扣留',
  ROLLING_RESERVE_RELEASE: '循環保證金釋放',
  ROLLING_RESERVE_OFFSET: '循環保證金沒收',
  ADMIN_RECHARGE: '管理員充值',
  BALANCE_CORRECTION: '帳務沖正',
}

const typeColors: Record<string, string> = {
  PAYMENT: 'green',
  SETTLEMENT: 'blue',
  PAYOUT: 'orange',
  FREEZE: 'red',
  UNFREEZE: 'cyan',
  CHARGEBACK: 'red',
  CHARGEBACK_FEE: 'volcano',
  CHARGEBACK_RECOVERY: 'green',
  ROLLING_RESERVE_RECOVERY: 'green',
  REFUND: 'orange',
  FEE: 'default',
  ROLLING_RESERVE: 'purple',
  ROLLING_RESERVE_RELEASE: 'geekblue',
  ROLLING_RESERVE_OFFSET: 'magenta',
  ADMIN_RECHARGE: 'green',
  BALANCE_CORRECTION: 'volcano',
}

export default function Transactions() {
  const [transactions, setTransactions] = useState<BalanceTransaction[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    size: 20,
    total: 0,
    totalPages: 0,
  })
  const [loading, setLoading] = useState(true)
  const [type, setType] = useState<string | undefined>()
  const [pageSize, setPageSize] = useState(20)

  const fetchTransactions = (page: number = 1, size: number = pageSize) => {
    setLoading(true)
    getTransactions({ page, size, type })
      .then((res) => {
        setTransactions(res.data.items)
        setPagination(res.data.pagination)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchTransactions()
  }, [type])

  const formatAmount = (amount: string | number, currency: string) => {
    const num = Number(amount)
    const value = formatDecimalAmount(String(amount).replace(/^[+-]/, ''))
    return num >= 0 ? `+${value} ${currency}` : `-${value} ${currency}`
  }

  const columns = [
    {
      key: 'type',
      header: '類型',
      render: (row: BalanceTransaction) => {
        const type = row['type']
        return (
          <StatusBadge color={typeColors[type]}>
            {typeLabels[type] || type}
          </StatusBadge>
        )
      },
    },
    {
      key: 'amount',
      header: '金額',
      className: 'text-right tabular-nums',
      render: (row: BalanceTransaction) => {
        const record = row
        return (
          <span
            style={{
              color: Number(record.amount) >= 0 ? '#52c41a' : '#ff4d4f',
            }}
          >
            {formatAmount(record.amount, record.currency)}
          </span>
        )
      },
    },
    {
      key: 'description',
      header: '描述',
      render: (row: BalanceTransaction) => row['description'] ?? '-',
    },
    {
      key: 'source',
      header: '關聯單據',
      render: (row: BalanceTransaction) => {
        const record = row
        return (
          <div>
            {record.relatedOrderId ? (
              <Link to={`/dashboard/orders/${record.relatedOrderId}`}>
                {record.relatedOrderId}
              </Link>
            ) : (
              record.sourceId || '-'
            )}
            {record.relatedOrderId && record.subscription && (
              <div style={{ marginTop: 4 }}>
                <StatusBadge color={'blue'}>
                  訂閱賬單 #{record.subscription.cycleNo}
                </StatusBadge>
                <span className="text-muted-foreground">
                  {billingReasonText[record.subscription.billingReason] ||
                    record.subscription.billingReason}
                </span>
              </div>
            )}
            {record.relatedOrderId &&
              record.sourceId &&
              record.sourceId !== record.relatedOrderId && (
                <div>
                  <span className="text-muted-foreground">
                    來源：{record.sourceId}
                  </span>
                </div>
              )}
          </div>
        )
      },
    },
    {
      key: 'createdAt',
      header: '時間',
      className: 'text-xs text-muted-foreground',
      render: (row: BalanceTransaction) => {
        const ts = row['createdAt']
        return formatTimestamp(ts)
      },
    },
  ]

  return (
    <div>
      <h2
        style={{ marginBottom: 16 }}
        className="mb-4 text-xl font-semibold tracking-tight"
      >
        交易明細
      </h2>
      <Card>
        <div className="space-y-4 px-5">
          <div style={{ width: 150, marginBottom: 16 }}>
            <SelectField
              aria-label="交易類型"
              options={[
                { value: '', label: '全部' },
                ...Object.entries(typeLabels).map(([value, label]) => ({
                  value,
                  label,
                })),
              ]}
              placeholder={'交易類型'}
              value={type}
              onValueChange={(value) => setType(value || undefined)}
            />
          </div>
          <DataTable
            rowKey={'id'}
            loading={loading}
            columns={columns}
            data={transactions}
            pagination={{
              page: pagination.page,
              size: pagination.size,
              total: pagination.total,
              onChange: (page, size) => {
                if (size !== pageSize) {
                  setPageSize(size)
                  fetchTransactions(1, size)
                } else {
                  fetchTransactions(page, size)
                }
              },
            }}
          />
        </div>
      </Card>
    </div>
  )
}
